import { Chip } from "@/components/ui/chip";
import { cn } from "@/lib/utils";
import type { ScenarioPersonaBindingData } from "./scenario-row-types";

const STALE_THRESHOLD_DAYS = 14;

export function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return null;
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}

export function formatAge(days: number): string {
  if (days === 0) return "today";
  if (days === 1) return "1d ago";
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  return months === 1 ? "1mo ago" : `${months}mo ago`;
}

export type BindingState =
  | { kind: "unrun" }
  | { kind: "clean"; ageDays: number; isStale: boolean; runCount: number }
  | {
      kind: "open";
      openCount: number;
      criticalCount: number;
      hasCritical: boolean;
      ageDays: number;
      isStale: boolean;
      runCount: number;
    };

export function deriveBindingState(
  binding: ScenarioPersonaBindingData,
): BindingState {
  const runCount = binding.runCount ?? 0;
  if (runCount === 0 || !binding.completedAt) {
    return { kind: "unrun" };
  }
  const ageDays = daysSince(binding.completedAt) ?? 0;
  const isStale = ageDays > STALE_THRESHOLD_DAYS;
  const open = binding.openFindingCount ?? 0;
  if (open === 0) {
    return { kind: "clean", ageDays, isStale, runCount };
  }
  const criticalCount =
    binding.criticalOpenCount ?? (binding.hasCriticalOpen ? 1 : 0);
  return {
    kind: "open",
    openCount: open,
    criticalCount,
    hasCritical: criticalCount > 0 || !!binding.hasCriticalOpen,
    ageDays,
    isStale,
    runCount,
  };
}

export function sortBindingsByUrgency(
  bindings: ScenarioPersonaBindingData[],
): ScenarioPersonaBindingData[] {
  function priority(b: ScenarioPersonaBindingData): number {
    const s = deriveBindingState(b);
    if (s.kind === "open" && s.hasCritical) return 0;
    if (s.kind === "open") return 1;
    if (s.kind === "unrun") return 2;
    if (s.kind === "clean" && s.isStale) return 3;
    return 4; // clean and fresh
  }
  return [...bindings].sort((a, b) => priority(a) - priority(b));
}

export function BindingStateLabel({ state }: { state: BindingState }) {
  if (state.kind === "unrun") {
    return <Chip tone="muted">Not run yet</Chip>;
  }

  if (state.kind === "clean") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs tabular-nums text-muted-foreground">
        <span
          aria-hidden="true"
          className="size-1.5 rounded-full bg-mark-helps"
        />
        {formatAge(state.ageDays)}
        {state.isStale && (
          <span className="text-severity-important">· stale</span>
        )}
      </span>
    );
  }

  // open
  return (
    <span className="inline-flex items-center gap-1.5 text-xs tabular-nums text-muted-foreground">
      <span
        aria-hidden="true"
        className={cn(
          "size-2 rounded-full",
          state.hasCritical ? "bg-mark-critical" : "bg-mark-important",
        )}
      />
      <span
        className={cn(
          "font-semibold",
          state.hasCritical ? "text-severity-critical" : "text-foreground",
        )}
      >
        {state.openCount} open
      </span>
      {state.criticalCount > 0 && (
        <span className="font-semibold text-severity-critical">
          · {state.criticalCount} critical
        </span>
      )}
      <span>· {formatAge(state.ageDays)}</span>
      {state.isStale && (
        <span className="text-severity-important">· stale</span>
      )}
    </span>
  );
}
